/**
 * Small presentational rails for CategoryPage: the category switcher,
 * hero banner, product-type chips, the sort + "new launch" bar, and
 * the inner Browse/Preferences/Saved tabs.
 *
 * All are stateless — parent owns state and passes callbacks.
 */
import React from "react";
import { Star, TrendingUp, Clock } from "lucide-react";

// Canonical sort options used across every section on the category page.
// Each entry maps to the per-endpoint sort key (products already support
// `rating`, stores support `rating`, streams/videos fall back to
// popularity signals since they don't have a rating field).
export const SORT_OPTS = [
  { id: "rating",  label: "Top rated", icon: Star,        color: "#D4FF00",
    products: "rating", stores: "rating", streams: "popular", videos: "popular" },
  { id: "popular", label: "Popular",   icon: TrendingUp,  color: "#FF007F",
    products: "best_selling", stores: "followers", streams: "popular", videos: "popular" },
  { id: "newest",  label: "Newest",    icon: Clock,       color: "#00F0FF",
    products: "newest", stores: "newest", streams: "newest", videos: "newest" },
];


export function CategorySwitcherRail({ cats, activeId, onSwitch }) {
  if (!cats?.length) return null;
  return (
    <div className="sticky top-[60px] z-30 -mx-4 px-4 py-2 bg-[#0F0817]/95 backdrop-blur border-b border-[#3D2845] mb-4"
         data-testid="cat-switcher">
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {cats.map((c) => {
          const active = c.category_id === activeId;
          return (
            <button key={c.category_id}
              onClick={() => onSwitch(c.category_id)}
              data-testid={`cat-switch-${c.category_id}`}
              className={`shrink-0 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition border ${
                active
                  ? "bg-[#D4FF00] text-black border-[#D4FF00] shadow-[0_6px_20px_rgba(212,255,0,0.35)]"
                  : "bg-[#261534] border-[#3D2845] text-[#A1A1AA] hover:border-[#D4FF00] hover:text-[#FAFAFA]"
              }`}>
              {c.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}


export function CategoryHero({ cat }) {
  if (!cat) return null;
  return (
    <div className="rounded-3xl overflow-hidden relative h-40 sm:h-56 mb-6" data-testid="cat-hero">
      <img src={cat.image} alt="" className="w-full h-full object-cover"/>
      <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent"/>
      <div className="absolute bottom-5 left-5">
        <div className="text-xs uppercase tracking-widest text-[#D4FF00] font-bold">Category</div>
        <h1 className="font-display font-black text-3xl sm:text-5xl uppercase">{cat.name}</h1>
      </div>
    </div>
  );
}


export function ProductTypeChips({ productTypes, activeType, onChange }) {
  if (!productTypes?.length) return null;
  return (
    <div className="mb-4" data-testid="cat-type-rail">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA]">
          Product type
        </span>
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
        <button
          onClick={() => onChange("")}
          data-testid="cat-type-all"
          className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition border ${
            activeType === ""
              ? "bg-[#00F0FF] text-black border-[#00F0FF] shadow-[0_4px_16px_rgba(0,240,255,0.35)]"
              : "bg-[#261534] border-[#3D2845] text-[#A1A1AA] hover:border-[#00F0FF] hover:text-[#FAFAFA]"
          }`}>
          All
        </button>
        {productTypes.map(t => {
          const active = t.type_id === activeType;
          return (
            <button key={t.type_id}
              onClick={() => onChange(active ? "" : t.type_id)}
              data-testid={`cat-type-${t.type_id}`}
              className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition border ${
                active
                  ? "bg-[#00F0FF] text-black border-[#00F0FF] shadow-[0_4px_16px_rgba(0,240,255,0.35)]"
                  : "bg-[#261534] border-[#3D2845] text-[#A1A1AA] hover:border-[#00F0FF] hover:text-[#FAFAFA]"
              }`}>
              {t.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}


export function SortAndNewLaunchBar({ sortId, onSortChange, newOnly, onToggleNewOnly }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-6 flex-wrap" data-testid="cat-sort-row">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => onToggleNewOnly(!newOnly)}
          data-testid="cat-new-launch-toggle"
          className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition border ${
            newOnly
              ? "bg-[#FF007F] text-white border-[#FF007F] shadow-[0_4px_16px_rgba(255,0,127,0.35)]"
              : "bg-[#261534] border-[#3D2845] text-[#A1A1AA] hover:border-[#FF007F] hover:text-[#FAFAFA]"
          }`}>
          🚀 New launch only
        </button>
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA]">
          Sort by
        </div>
      </div>
      <div className="flex gap-1.5 p-1 bg-[#261534] border border-[#3D2845] rounded-full">
        {SORT_OPTS.map(o => {
          const I = o.icon;
          const active = sortId === o.id;
          return (
            <button key={o.id} onClick={() => onSortChange(o.id)}
              data-testid={`cat-sort-${o.id}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition ${
                active ? "text-black" : "text-[#A1A1AA] hover:text-[#FAFAFA]"
              }`}
              style={active ? { background: o.color } : {}}>
              <I size={12}/>
              <span>{o.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}


export function InnerTab({ id, active, onClick, icon: Icon, label, color, badge }) {
  const isActive = active === id;
  return (
    <button
      onClick={() => onClick(id)}
      data-testid={`cat-inner-tab-${id}`}
      className={`shrink-0 flex items-center gap-2 px-4 py-3 text-sm font-bold uppercase tracking-wider border-b-2 transition ${
        isActive
          ? ""
          : "border-transparent text-[#A1A1AA] hover:text-[#FAFAFA]"
      }`}
      style={isActive ? { color, borderColor: color } : {}}>
      <Icon size={14}/>
      {label}
      {badge != null && badge > 0 && (
        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] px-1.5 bg-[#FF007F] text-white">
          {badge}
        </span>
      )}
    </button>
  );
}


export function Section({ title, icon: Icon, accent, children, cta, testid }) {
  const parts = title.split(" · ");
  return (
    <section className="mb-10" data-testid={testid}>
      <div className="flex items-end justify-between mb-3 sm:mb-4">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={18} style={{ color: accent }}/>}
          <h2 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight">
            {parts[0]}
            {parts[1] && (
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA] ml-2 hidden sm:inline">
                · {parts[1]}
              </span>
            )}
            <span style={{ color: accent }}>.</span>
          </h2>
        </div>
        {cta || null}
      </div>
      {children}
    </section>
  );
}
